import { Component, Input } from '@angular/core';
import { SvgLoaderComponent } from "../../../ui-kit/images/svg-loader/svg-loader.component";
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { getFriendlyRoute } from 'app/utils/flight/flight-data-utils';
import { TimeAndDateService } from 'app/services/time-and-date-service';
import { UserService } from 'app/services/user.service';
import { AppStateService } from 'app/services/app-state.service';
import { FlightOptionService } from 'app/services/flight-option-service';
import { PrimaryBtnComponent } from 'app/components/ui-kit/buttons/primary-btn/primary-btn.component';
import { PopTrayComponent } from 'app/components/ui-kit/popovers/pop-tray/pop-tray.component';
import { BaseTextAreaComponent } from 'app/components/ui-kit/inputs/base-text-area/base-text-area.component';
import { StarRatingComponent } from 'app/components/ui-kit/inputs/star-rating/star-rating.component';
import { UserInteractionService } from 'app/services/user-interaction.service';

@Component({
  selector: 'app-flight-connection-card',
  standalone: true,
  imports: [SvgLoaderComponent, CommonModule, FormsModule, PrimaryBtnComponent, PopTrayComponent, BaseTextAreaComponent, StarRatingComponent],
  templateUrl: './flight-connection-card.component.html',
  styleUrl: './flight-connection-card.component.scss'
})
export class FlightConnectionCardComponent {
  @Input() connection: any;

  friendlyRoute: string;
  fromLocation: string;
  toLocation: string = '';
  viewedAt: string = null;
  otherUserName: string = null;
  dateDisplay: string = null;
  timeDisplay: string = null;
  flightOption: any = null;
  otherUserId: string = null;
  showReviewTray: boolean = false;
  reviewActions: any;
  reviewRating: number = 5;
  reviewText: string = '';
  userHasReviewed: boolean = false;

  constructor(private timeAndDate: TimeAndDateService, private userService: UserService, private appState: AppStateService,
    private flightOptionService: FlightOptionService, private userInteraction: UserInteractionService) {}

  ngOnInit(): void {
    this.translateRoute();
    this.getNameInformation();
    this.checkConnectionState();

    if (this.connection.status !== 'pending') {
      this.getDateInformation();
    }

    this.reviewActions = {
      primaryButtons: [
        { text: 'Submit Review', function: () => this.handleSubmitReview() },
      ],
      secondaryButtons: [
        { text: 'Cancel', function: () => this.onClosePopTray() },
      ],
      closeFunction: () => this.onClosePopTray(),
    };
  }

  translateRoute() {
    this.fromLocation = this.connection.fromLocation;
    this.toLocation = this.connection.toLocation;
    if (this.toLocation === 'Beach' || this.toLocation === 'Mountain') {
      const loc = this.toLocation;
      this.toLocation = `Any ${loc}`;
    }
    const route = getFriendlyRoute(this.fromLocation, { locationName: this.toLocation });
    this.friendlyRoute = route;
  }

  async getNameInformation() {
    try {
      const currentUser = await this.userService.getUserProfile();
      if (!currentUser) {
        return;
      }
      const otherUserId = this.connection.fromId === currentUser.id ? this.connection.toId : this.connection.fromId;
      const otherUserProfile = await this.userService.getProfileById(otherUserId);
      this.otherUserName = otherUserProfile.displayName;
      this.otherUserId = otherUserId;
      await this.checkReviewStatus();
    } catch (err) {
      console.error('unable to load connection user data', err);
    }
  }

  checkConnectionState() {
    if (this.connection?.firstViewedOn) {
      const jsViewedOn = this.connection.firstViewedOn.toDate();
      const timeAgo = this.timeAndDate.getFriendlyTimeAgo(jsViewedOn);
      this.viewedAt = `Seen ${timeAgo}`;
    }
  }

  async getDateInformation() {
    try {
      let option = this.connection.flightOption;
      if (!option) {
        const optionId = this.connection.toUserFlightOptionId || this.connection.fromUserFlightOptionId;
        if (optionId) {
          option = await this.flightOptionService.getFlightOption(optionId);
        }
      }
      if (!option) {
        return;
      }

      this.flightOption = option;

      const tz = this.timeAndDate.getTimezoneAbbreviation(option.timeZone);
      if (option.departureDateExact) {
        this.dateDisplay = this.timeAndDate.formatDateToMMDDYY(option.departureDateExact);
      } else if (option.departureDateRangeStart && option.departureDateRangeEnd) {
        const start = this.timeAndDate.formatDateToMMDDYY(option.departureDateRangeStart);
        const end = this.timeAndDate.formatDateToMMDDYY(option.departureDateRangeEnd);
        this.dateDisplay = `${start} - ${end}`;
      }

      if (option.departureTimeExact) {
        const time = this.timeAndDate.formatTimeForTimezone(option.timeZone, option.departureTimeExact);
        this.timeDisplay = `${time} ${tz}`;
      } else if (option.departureTimeRangeStart && option.departureTimeRangeEnd) {
        const startTime = this.timeAndDate.formatTimeForTimezone(option.timeZone, option.departureTimeRangeStart);
        const endTime = this.timeAndDate.formatTimeForTimezone(option.timeZone, option.departureTimeRangeEnd);
        this.timeDisplay = `${startTime} - ${endTime} - ${tz}`;
      }
    } catch (err) {
      console.error('unable to load flight option', err);
    }
  }

  async checkReviewStatus() {
    if (!this.connection?.id || !this.otherUserId) {
      return;
    }
    try {
      const alreadyReviewed = await this.userInteraction.hasUserBeenReviewed(this.otherUserId, this.connection.id);
      this.userHasReviewed = alreadyReviewed;
    } catch (err) {
      console.error('unable to determine review state', err);
    }
  }

  isFlightReadyToReview() {
    if (!this.flightOption || this.userHasReviewed) {
      return false;
    }

    const dateStr = this.flightOption.departureDateExact || this.flightOption.departureDateRangeEnd;
    if (!dateStr) {
      return false;
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const flightDate = new Date(dateStr);
    flightDate.setHours(23, 59, 59, 999);

    return flightDate < today;
  }

  showReview(event: Event) {
    event.stopPropagation();
    this.showReviewTray = true;
  }

  onClosePopTray() {
    this.showReviewTray = false;
    this.reviewText = '';
    this.reviewRating = 5;
  }

  async handleSubmitReview() {
    if (!this.connection?.id || !this.otherUserId) {
      return;
    }

    try {
      await this.userInteraction.reviewUser(this.otherUserId, this.connection.id, this.reviewRating, this.reviewText);
      this.userHasReviewed = true;
    } catch (err) {
      console.error('Failed to submit review', err);
    } finally {
      this.onClosePopTray();
    }
  }

}
